import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Plus, Trash2, GripVertical, ChevronDown, ChevronUp, 
  Edit, Eye, EyeOff, Settings, ArrowUp, ArrowDown
} from 'lucide-react';

import { FIELD_TYPES, CONDITION_OPERATORS, PREDEFINED_LISTS, REPEATABLE_GROUP_TEMPLATES } from './FormBuilder/constants';
import FieldEditorModal from './FormBuilder/FieldEditorModal';

const FormBuilder = ({ formConfig, onChange }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [fieldDialog, setFieldDialog] = useState({ open: false, sectionId: null, field: null, fieldIndex: null });
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverField, setDragOverField] = useState(null);
  
  // State for adding custom sub-fields

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const isSectionExpanded = (sectionId) => expandedSections.has(sectionId);

  const toggleSectionEnabled = (sectionId) => {
    const updated = formConfig.map(section =>
      section.id === sectionId ? { ...section, enabled: !section.enabled } : section
    );
    onChange(updated);
  };

  const updateSectionTitle = (sectionId, title) => {
    const updated = formConfig.map(section =>
      section.id === sectionId ? { ...section, title } : section
    );
    onChange(updated);
  };

  const updateSectionDescription = (sectionId, description) => {
    const updated = formConfig.map(section =>
      section.id === sectionId ? { ...section, description } : section
    );
    onChange(updated);
  };

  const openFieldDialog = (sectionId, field = null, fieldIndex = null) => {
    setFieldDialog({ open: true, sectionId, field, fieldIndex });
  };

  const saveField = (fieldData) => {
    const { sectionId, fieldIndex } = fieldDialog;
    
    const updated = formConfig.map(section => {
      if (section.id === sectionId) {
        const fields = [...section.fields];
        if (fieldIndex !== null) {
          fields[fieldIndex] = fieldData;
        } else {
          fields.push(fieldData);
        }
        return { ...section, fields };
      }
      return section;
    });
    
    onChange(updated);
    setFieldDialog({ open: false, sectionId: null, field: null, fieldIndex: null });
  };

  const deleteField = (sectionId, fieldIndex) => {
    const updated = formConfig.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: section.fields.filter((_, i) => i !== fieldIndex)
        };
      }
      return section;
    });
    onChange(updated);
  };


  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      description: '',
      enabled: true,
      fields: []
    };
    onChange([...formConfig.slice(0, -2), newSection, ...formConfig.slice(-2)]);
  };

  const deleteSection = (sectionId) => {
    // Don't allow deleting sponsor and review sections
    if (sectionId === 'sponsor' || sectionId === 'review') {
      return;
    }
    const updated = formConfig.filter(section => section.id !== sectionId);
    onChange(updated);
  };

  // Section drag and drop handlers
  const handleSectionDragStart = (e, sectionIndex) => {
    // Only start section drag if not dragging a field
    if (draggedField) return;
    setDraggedSection(sectionIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/section', String(sectionIndex));
  };

  const handleSectionDragOver = (e, sectionIndex) => {
    e.preventDefault();
    // Handle section drag
    if (draggedSection !== null && draggedSection !== sectionIndex) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverSection(sectionIndex);
    }
  };

  const handleSectionDragLeave = (e) => {
    // Only clear if leaving the section entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverSection(null);
    }
  };

  const handleSectionDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only handle section drops, not field drops
    if (draggedSection === null || draggedSection === targetIndex) {
      setDraggedSection(null);
      setDragOverSection(null);
      return;
    }

    const newConfig = [...formConfig];
    const [draggedItem] = newConfig.splice(draggedSection, 1);
    
    // Adjust target index if dragging from before to after
    const adjustedTargetIndex = draggedSection < targetIndex ? targetIndex : targetIndex;
    newConfig.splice(adjustedTargetIndex, 0, draggedItem);
    
    onChange(newConfig);
    setDraggedSection(null);
    setDragOverSection(null);
  };

  const handleSectionDragEnd = () => {
    setDraggedSection(null);
    setDragOverSection(null);
  };

  // Move section up/down with buttons (more reliable than drag)
  const moveSectionUp = (index) => {
    if (index <= 0) return;
    const newConfig = [...formConfig];
    [newConfig[index - 1], newConfig[index]] = [newConfig[index], newConfig[index - 1]];
    onChange(newConfig);
  };

  const moveSectionDown = (index) => {
    if (index >= formConfig.length - 1) return;
    const newConfig = [...formConfig];
    [newConfig[index], newConfig[index + 1]] = [newConfig[index + 1], newConfig[index]];
    onChange(newConfig);
  };

  // Move field up/down with buttons
  const moveFieldUp = (sectionId, fieldIndex) => {
    if (fieldIndex <= 0) return;
    const newConfig = formConfig.map(section => {
      if (section.id === sectionId) {
        const newFields = [...section.fields];
        [newFields[fieldIndex - 1], newFields[fieldIndex]] = [newFields[fieldIndex], newFields[fieldIndex - 1]];
        return { ...section, fields: newFields };
      }
      return section;
    });
    onChange(newConfig);
  };

  const moveFieldDown = (sectionId, fieldIndex) => {
    const section = formConfig.find(s => s.id === sectionId);
    if (!section || fieldIndex >= section.fields.length - 1) return;
    const newConfig = formConfig.map(s => {
      if (s.id === sectionId) {
        const newFields = [...s.fields];
        [newFields[fieldIndex], newFields[fieldIndex + 1]] = [newFields[fieldIndex + 1], newFields[fieldIndex]];
        return { ...s, fields: newFields };
      }
      return s;
    });
    onChange(newConfig);
  };

  // Move field to another section
  const moveFieldToSection = (fromSectionId, fieldIndex, toSectionId) => {
    const newConfig = formConfig.map(section => ({ ...section, fields: [...section.fields] }));
    const fromSection = newConfig.find(s => s.id === fromSectionId);
    const toSection = newConfig.find(s => s.id === toSectionId);
    
    if (fromSection && toSection) {
      const [movedField] = fromSection.fields.splice(fieldIndex, 1);
      toSection.fields.push(movedField);
      onChange(newConfig);
    }
  };

  // Field drag and drop handlers
  const handleFieldDragStart = (e, sectionId, fieldIndex) => {
    e.stopPropagation();
    setDraggedField({ sectionId, fieldIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${sectionId}-${fieldIndex}`);
  };

  const handleFieldDragOver = (e, sectionId, fieldIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedField && (draggedField.sectionId !== sectionId || draggedField.fieldIndex !== fieldIndex)) {
      setDragOverField({ sectionId, fieldIndex });
    }
  };

  const handleFieldDragLeave = () => {
    setDragOverField(null);
  };

  const handleFieldDrop = (e, targetSectionId, targetFieldIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedField) {
      setDraggedField(null);
      setDragOverField(null);
      return;
    }
    
    // Don't drop on same position
    if (draggedField.sectionId === targetSectionId && draggedField.fieldIndex === targetFieldIndex) {
      setDraggedField(null);
      setDragOverField(null);
      return;
    }

    const newConfig = formConfig.map(section => ({ ...section, fields: [...section.fields] }));
    
    // Find source and target sections
    const sourceSection = newConfig.find(s => s.id === draggedField.sectionId);
    const targetSection = newConfig.find(s => s.id === targetSectionId);
    
    if (sourceSection && targetSection) {
      // Remove from source
      const [movedField] = sourceSection.fields.splice(draggedField.fieldIndex, 1);
      
      // Adjust target index if moving within same section and source is before target
      let adjustedTargetIndex = targetFieldIndex;
      if (draggedField.sectionId === targetSectionId && draggedField.fieldIndex < targetFieldIndex) {
        adjustedTargetIndex--;
      }
      
      // If targetFieldIndex is -1, append to end of section
      if (targetFieldIndex === -1) {
        targetSection.fields.push(movedField);
      } else {
        // Insert at target
        targetSection.fields.splice(adjustedTargetIndex, 0, movedField);
      }
      
      onChange(newConfig);
    }
    
    setDraggedField(null);
    setDragOverField(null);
  };

  // Handler for dropping on the section drop zone (append to end)
  const handleSectionDropZoneDragOver = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedField) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverField({ sectionId, fieldIndex: -1 }); // -1 indicates drop zone
    }
  };

  const handleSectionDropZoneDrop = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    handleFieldDrop(e, sectionId, -1); // -1 to append at end
  };

  // Auto-expand section when dragging field over section header
  const handleSectionHeaderDragOver = (e, sectionId) => {
    e.preventDefault();
    if (draggedField && sectionId !== 'sponsor' && sectionId !== 'review') {
      e.dataTransfer.dropEffect = 'move';
      // Auto-expand section after hovering for a moment
      if (!isSectionExpanded(sectionId)) {
        setExpandedSections(prev => {
          const newSet = new Set(prev);
          newSet.add(sectionId);
          return newSet;
        });
      }
      setDragOverField({ sectionId, fieldIndex: -1 });
    }
  };

  const handleSectionHeaderDrop = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedField && sectionId !== 'sponsor' && sectionId !== 'review') {
      handleFieldDrop(e, sectionId, -1);
    }
  };

  const handleFieldDragEnd = () => {
    setDraggedField(null);
    setDragOverField(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800">Application Form Sections</h3>
          <p className="text-sm text-slate-500">Drag sections or fields to reorder. Enable/disable sections and customize fields.</p>
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addSection}
          className="rounded-sm"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Section
        </Button>
      </div>

      {formConfig.map((section, sectionIndex) => (
        <div 
          key={section.id} 
          className={`border rounded-sm overflow-hidden transition-all duration-200 ${
            section.enabled ? 'border-slate-200' : 'border-slate-100 bg-slate-50'
          } ${
            dragOverSection === sectionIndex ? 'border-greek-blue border-2 ring-2 ring-greek-blue ring-opacity-20' : ''
          } ${
            draggedSection === sectionIndex ? 'opacity-50 scale-[0.98]' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            if (draggedSection !== null && draggedSection !== sectionIndex) {
              handleSectionDragOver(e, sectionIndex);
            }
          }}
          onDragLeave={(e) => handleSectionDragLeave(e)}
          onDrop={(e) => {
            if (draggedSection !== null) {
              handleSectionDrop(e, sectionIndex);
            }
          }}
        >
          {/* Section Header */}
          <div 
            className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
              section.enabled ? 'bg-white hover:bg-slate-50' : 'bg-slate-100'
            } ${
              draggedField && dragOverField?.sectionId === section.id && !isSectionExpanded(section.id)
                ? 'bg-blue-50 border-b-2 border-greek-blue'
                : ''
            } ${
              draggedSection !== null && dragOverSection === sectionIndex
                ? 'bg-blue-50'
                : ''
            }`}
            onClick={() => toggleSection(section.id)}
            onDragOver={(e) => {
              // Handle field drag over section header
              if (draggedField) {
                handleSectionHeaderDragOver(e, section.id);
              }
            }}
            onDrop={(e) => {
              // Handle field drop on section header
              if (draggedField) {
                handleSectionHeaderDrop(e, section.id);
              }
            }}
          >
            <div className="flex items-center gap-3">
              <div
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  handleSectionDragStart(e, sectionIndex);
                }}
                onDragEnd={handleSectionDragEnd}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="w-4 h-4 text-slate-400" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={section.enabled}
                  onCheckedChange={() => toggleSectionEnabled(section.id)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={section.id === 'sponsor' || section.id === 'review'}
                />
                <span className={`font-medium ${section.enabled ? 'text-slate-800' : 'text-slate-500'}`}>
                  {section.title}
                </span>
                {!section.enabled && (
                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Disabled</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Move section up/down buttons */}
              <div className="flex flex-col -space-y-1 mr-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); moveSectionUp(sectionIndex); }}
                  disabled={sectionIndex === 0}
                  className="h-5 w-5 p-0 hover:bg-slate-200 disabled:opacity-30"
                  title="Move section up"
                >
                  <ArrowUp className="w-3 h-3 text-slate-500" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); moveSectionDown(sectionIndex); }}
                  disabled={sectionIndex === formConfig.length - 1}
                  className="h-5 w-5 p-0 hover:bg-slate-200 disabled:opacity-30"
                  title="Move section down"
                >
                  <ArrowDown className="w-3 h-3 text-slate-500" />
                </Button>
              </div>
              <span className="text-sm text-slate-500">{section.fields.length} fields</span>
              {isSectionExpanded(section.id) ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </div>
          </div>

          {/* Section Content */}
          {isSectionExpanded(section.id) && (
            <div className="border-t border-slate-200 p-4 bg-slate-50">
              {/* Section Settings */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                  <Label className="text-xs">Section Title</Label>
                  <Input
                    value={section.title}
                    onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                    className="rounded-sm h-8 text-sm"
                    disabled={section.id === 'sponsor' || section.id === 'review'}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={section.description}
                    onChange={(e) => updateSectionDescription(section.id, e.target.value)}
                    className="rounded-sm h-8 text-sm"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Fields List */}
              {section.fields.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {section.fields.map((field, fieldIndex) => (
                    <div 
                      key={field.id} 
                      className={`flex items-center justify-between p-3 bg-white rounded-sm border transition-all duration-200 ${
                        dragOverField?.sectionId === section.id && dragOverField?.fieldIndex === fieldIndex
                          ? 'border-greek-blue border-2 bg-blue-50'
                          : 'border-slate-200'
                      } ${
                        draggedField?.sectionId === section.id && draggedField?.fieldIndex === fieldIndex
                          ? 'opacity-50'
                          : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleFieldDragStart(e, section.id, fieldIndex)}
                      onDragOver={(e) => handleFieldDragOver(e, section.id, fieldIndex)}
                      onDragLeave={handleFieldDragLeave}
                      onDrop={(e) => handleFieldDrop(e, section.id, fieldIndex)}
                      onDragEnd={handleFieldDragEnd}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col -space-y-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); moveFieldUp(section.id, fieldIndex); }}
                            disabled={fieldIndex === 0}
                            className="h-4 w-4 p-0 hover:bg-slate-200 disabled:opacity-30"
                            title="Move field up"
                          >
                            <ArrowUp className="w-3 h-3 text-slate-400" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); moveFieldDown(section.id, fieldIndex); }}
                            disabled={fieldIndex === section.fields.length - 1}
                            className="h-4 w-4 p-0 hover:bg-slate-200 disabled:opacity-30"
                            title="Move field down"
                          >
                            <ArrowDown className="w-3 h-3 text-slate-400" />
                          </Button>
                        </div>
                        <GripVertical className="w-4 h-4 text-slate-300 cursor-grab active:cursor-grabbing" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-700">{field.label}</span>
                            {field.required && (
                              <span className="text-xs text-red-500">*Required</span>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">{FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Move to section dropdown */}
                        <Select
                          value=""
                          onValueChange={(targetSectionId) => moveFieldToSection(section.id, fieldIndex, targetSectionId)}
                        >
                          <SelectTrigger className="h-8 w-8 p-0 border-0 bg-transparent hover:bg-slate-100 rounded" title="Move to section">
                            <GripVertical className="w-4 h-4 text-slate-400 mx-auto" />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="px-2 py-1 text-xs text-slate-500 font-medium">Move to section:</div>
                            {formConfig
                              .filter(s => s.id !== section.id && s.id !== 'sponsor' && s.id !== 'review')
                              .map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openFieldDialog(section.id, field, fieldIndex)}
                          className="h-8 w-8 p-0"
                          data-testid={`formbuilder-edit-field-${section.id}-${field.id || fieldIndex}`}
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteField(section.id, fieldIndex)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Drop zone at end of section for moving fields between sections */}
                  {draggedField && section.id !== 'sponsor' && section.id !== 'review' && (
                    <div
                      className={`p-4 rounded-sm border-2 border-dashed transition-all duration-200 text-center ${
                        dragOverField?.sectionId === section.id && dragOverField?.fieldIndex === -1
                          ? 'border-greek-blue bg-blue-50 text-greek-blue'
                          : 'border-slate-300 text-slate-400'
                      }`}
                      onDragOver={(e) => handleSectionDropZoneDragOver(e, section.id)}
                      onDragLeave={handleFieldDragLeave}
                      onDrop={(e) => handleSectionDropZoneDrop(e, section.id)}
                    >
                      <span className="text-sm">Drop field here to add to this section</span>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={`text-sm mb-4 text-center py-4 bg-white rounded-sm border-2 border-dashed transition-all duration-200 ${
                    draggedField && dragOverField?.sectionId === section.id && dragOverField?.fieldIndex === -1
                      ? 'border-greek-blue bg-blue-50 text-greek-blue'
                      : 'border-slate-300 text-slate-500'
                  }`}
                  onDragOver={(e) => section.id !== 'sponsor' && section.id !== 'review' && handleSectionDropZoneDragOver(e, section.id)}
                  onDragLeave={handleFieldDragLeave}
                  onDrop={(e) => section.id !== 'sponsor' && section.id !== 'review' && handleSectionDropZoneDrop(e, section.id)}
                >
                  {draggedField && section.id !== 'sponsor' && section.id !== 'review' 
                    ? 'Drop field here to add to this section' 
                    : 'No fields in this section'}
                </div>
              )}

              {/* Add Field Button */}
              {section.id !== 'sponsor' && section.id !== 'review' && (
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openFieldDialog(section.id)}
                    className="rounded-sm"
                    data-testid={`formbuilder-add-field-${section.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Field
                  </Button>
                  {section.id !== 'personal_info' && section.id !== 'academic_info' && section.id !== 'activities' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSection(section.id)}
                      className="rounded-sm text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Section
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Field Editor Dialog */}
      <FieldEditorModal 
        open={fieldDialog.open} 
        onClose={() => setFieldDialog({ ...fieldDialog, open: false })} 
        onSave={saveField}
        initialField={fieldDialog.field}
        formConfig={formConfig}
      />
    </div>
  );
};

export { PREDEFINED_LISTS, REPEATABLE_GROUP_TEMPLATES } from './FormBuilder/constants';
export default FormBuilder;
